<script setup>
import { shouldShowVerificationReminder, verificationReminderDismissalKey, verificationReminderEmail } from "~/utils/verification-reminder.mjs"

const { data: session } = useAuth()
const { $posthog: posthog } = useNuxtApp()
const dismissed = ref(false)
const dismissalLoaded = ref(false)
const sending = ref(false)
const completionMessage = ref("")
const viewed = ref(false)

const dismissalKey = computed(() => verificationReminderDismissalKey(session.value))
const visible = computed(() => dismissalLoaded.value && shouldShowVerificationReminder(session.value, dismissed.value))
const dismissedResendVisible = computed(() => dismissalLoaded.value && dismissed.value && shouldShowVerificationReminder(session.value))
const email = computed(() => verificationReminderEmail(session.value))

const dismiss = () => {
	dismissed.value = true
	if (process.client && dismissalKey.value) sessionStorage.setItem(dismissalKey.value, "1")
	posthog?.capture("verification_reminder_dismissed", { source: "authenticated_shell", account_method: "email_password" })
}

const resend = async () => {
	if (sending.value || !email.value) return
	sending.value = true
	completionMessage.value = ""
	const source = dismissed.value ? "dismissed_reminder" : "authenticated_shell"
	posthog?.capture("verification_resend_requested", { source, account_method: "email_password" })
	try {
		const result = await $fetch("/api/credentials/resend-verification", {
			method: "POST",
			body: { email: email.value },
		})
		completionMessage.value = result.message
		posthog?.capture("verification_resend_completed", { source, account_method: "email_password" })
	} catch (error) {
		completionMessage.value = error?.data?.statusMessage || "Please try again later."
	} finally {
		sending.value = false
	}
}

watch(dismissalKey, (key) => {
	dismissalLoaded.value = false
	dismissed.value = false
	viewed.value = false
	completionMessage.value = ""
	if (!key || !process.client) return
	dismissed.value = sessionStorage.getItem(key) === "1"
	dismissalLoaded.value = true
}, { immediate: true })

watch(visible, (isVisible) => {
	if (!isVisible || viewed.value) return
	viewed.value = true
	posthog?.capture("verification_reminder_viewed", { source: "authenticated_shell", account_method: "email_password" })
}, { immediate: true })
</script>

<template>
	<section v-if="visible" class="mx-auto mt-4 max-w-7xl px-4 sm_px-6" aria-label="Email verification reminder">
		<div class="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
			<p class="mr-auto">Verify your email to help protect sensitive account changes.</p>
			<button class="rounded-md bg-amber-700 px-3 py-2 font-semibold text-white transition hover_bg-amber-800 disabled_cursor-not-allowed disabled_opacity-60" type="button" :disabled="sending" @click="resend">
				{{ sending ? "Sending…" : "Resend verification email" }}
			</button>
			<button class="px-2 py-1 font-semibold text-amber-900 underline" type="button" @click="dismiss">Dismiss</button>
			<p v-if="completionMessage" class="w-full" role="status">{{ completionMessage }}</p>
		</div>
	</section>
	<section v-else-if="dismissedResendVisible" class="mx-auto mt-4 max-w-7xl px-4 sm_px-6" aria-label="Email verification">
		<div class="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-sm text-slate-600">
			<span>Email verification is still pending.</span>
			<button class="font-semibold text-amber-800 underline transition hover_text-amber-900 disabled_cursor-not-allowed disabled_opacity-60" type="button" :disabled="sending" @click="resend">
				{{ sending ? "Sending…" : "Resend verification email" }}
			</button>
			<p v-if="completionMessage" class="w-full text-right" role="status">{{ completionMessage }}</p>
		</div>
	</section>
</template>
